import { hierarchyApi } from '@/modules/hierarchy/api/hierarchy-api'
import {
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/api-error-message'
import { getSessionEpoch, useAuth } from '@/lib/auth'
import { assistantEffects } from '@/lib/assistant-effects.generated'
import {
  type CreateEntityInput,
  type EntitySearchResult,
} from '@/modules/hierarchy/api/hierarchy-api'
import { type PagedResponse } from '@/lib/api/pagination'
import { HIERARCHY_PERMISSIONS } from '@/lib/permissions'
import { usePermissions } from '@/lib/use-permissions'
import { useSelectedEntityId } from '@/lib/use-selected-entity'

import { useEntityRecents } from './use-entity-recents'

export const entityKeys = {
  root: ['entities'],
  detail: (entityId: string) => ['entities', 'detail', entityId],
  ancestors: (entityId: string) => ['entities', 'ancestors', entityId],
  children: (entityId: string) => ['entities', 'children', entityId],
  search: (entityId: string, query: string) => ['entities', 'search', entityId, query],
  searchAcrossRoots: (rootIds: string[], query: string) => [
    'entities',
    'search',
    'across-roots',
    rootIds,
    query,
  ],
  brandingRoot: ['entities', 'branding'],
  branding: (entityId: string) => ['entities', 'branding', entityId],
  settings: (entityId: string) => ['entities', 'settings', entityId],
  capabilities: (entityId: string) => ['entities', 'capabilities', entityId],
  settingHistoryByEntity: (entityId: string) => [
    'entities',
    'setting-history',
    entityId,
  ],
  settingHistory: (entityId: string, settingKey: string) => [
    'entities',
    'setting-history',
    entityId,
    settingKey,
  ],
  roots: ['entities', 'roots'],
  homeRoots: (sessionEpoch: number, userId?: string) => ['entities', 'roots', 'home', sessionEpoch, userId],
}

// Shortest query that triggers a search; mirrored server-side as a backstop.
export const SEARCH_MIN_CHARS = 3

const assistantEffectMeta = {
  assistantEffects: [
    assistantEffects.hierarchyChanged,
    assistantEffects.settingsChanged,
  ],
} as const

// `skipToken` (not `enabled`) keeps the query disabled *and* type-safe when no
// entity is selected — inside the function branch `entityId` is a non-null
// string, so there's no need to assert it.
export function useEntity(entityId: string | null) {
  return useQuery({
    queryKey: entityKeys.detail(entityId ?? ''),
    queryFn: entityId === null ? skipToken : () => hierarchyApi.getEntity(entityId),
    meta: assistantEffectMeta,
  })
}

export function useEntityAncestors(entityId: string | null) {
  return useQuery({
    queryKey: entityKeys.ancestors(entityId ?? ''),
    queryFn: entityId === null ? skipToken : () => hierarchyApi.getAncestors(entityId),
    meta: assistantEffectMeta,
  })
}

export function useRoots(enabled: boolean) {
  return useQuery({
    queryKey: entityKeys.roots,
    queryFn: enabled ? () => hierarchyApi.getRoots() : skipToken,
    meta: assistantEffectMeta,
  })
}

export function useHomeRoots() {
  const { user } = useAuth()
  return useQuery({
    queryKey: entityKeys.homeRoots(getSessionEpoch(), user?.userId),
    queryFn: ({ signal }) => hierarchyApi.getRoots(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    meta: assistantEffectMeta,
  })
}

const BRANDING_REFRESH_INTERVAL_MS = 30_000

export function useEntityBranding(entityId: string | null) {
  return useQuery({
    queryKey: entityKeys.branding(entityId ?? ''),
    queryFn:
      entityId === null ? skipToken : () => hierarchyApi.getEntityBranding(entityId),
    select: (branding) => branding.value,
    refetchInterval: (query) => {
      const refreshStatus = query.state.data?.refreshStatus
      return refreshStatus === 'Pending' || refreshStatus === 'Running'
        ? BRANDING_REFRESH_INTERVAL_MS
        : false
    },
    staleTime: 5 * 60_000,
    meta: assistantEffectMeta,
  })
}

// One page of an entity's children per call, cursor-paginated for "show more".
// `enabled` is the lazy gate: a tree node only fetches once expanded. Cursors
// are carried as strings ('' = first page) so the page-param type stays string
// without an assertion.
export function useEntityChildren(entityId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: entityKeys.children(entityId),
    queryFn: enabled
      ? ({ pageParam }) =>
          hierarchyApi.getEntityChildren(entityId, pageParam || undefined)
      : skipToken,
    initialPageParam: '',
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasNextPage
        ? (lastPage.pageInfo.endCursor ?? undefined)
        : undefined,
    meta: assistantEffectMeta,
  })
}

export function useSelectedEntity() {
  const entityId = useSelectedEntityId()
  return {
    entityId,
    entity: useEntity(entityId),
    ancestors: useEntityAncestors(entityId),
  }
}

export function useSearchEntities(entityId: string | null, query: string) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: entityKeys.search(entityId ?? '', trimmed),
    queryFn:
      entityId !== null && trimmed.length >= SEARCH_MIN_CHARS
        ? () => hierarchyApi.searchEntities(entityId, trimmed)
        : skipToken,
    meta: assistantEffectMeta,
  })
}

// One subtree search per readable root, merged here. Most users hold a single
// root; add a caller-scoped server endpoint if that stops being true.
export function useSearchEntitiesAcrossRoots(rootIds: string[], query: string) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: entityKeys.searchAcrossRoots(rootIds, trimmed),
    queryFn:
      rootIds.length > 0 && trimmed.length >= SEARCH_MIN_CHARS
        ? () => searchAcrossRoots(rootIds, trimmed)
        : skipToken,
    meta: assistantEffectMeta,
  })
}

async function searchAcrossRoots(
  rootIds: string[],
  query: string,
): Promise<PagedResponse<EntitySearchResult>> {
  const pages = await Promise.all(
    rootIds.map((rootId) => hierarchyApi.searchEntities(rootId, query)),
  )
  const items = pages
    .flatMap((page) => page.items)
    .sort(
      (left, right) =>
        left.name.localeCompare(right.name) ||
        left.entityId.localeCompare(right.entityId),
    )
  return {
    items,
    pageInfo: {
      hasNextPage: pages.some((page) => page.pageInfo.hasNextPage),
      endCursor: null,
    },
  }
}

export function createEntityErrorMessage(error: unknown) {
  return apiErrorMessage(error, 'Couldn’t create the entity. Try again.')
}

export function useCreateEntity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateEntityInput) => hierarchyApi.createEntity(input),
    onSuccess: () => {
      // A new child shifts parent/child and ancestor views; drop cached entity
      // data so anything on screen refetches.
      void queryClient.invalidateQueries({ queryKey: entityKeys.root })
    },
  })
}

export function renameEntityErrorMessage(error: unknown) {
  return apiErrorMessage(error, 'Couldn’t rename the entity. Try again.')
}

export function useRenameEntity() {
  const queryClient = useQueryClient()
  const { rename: renameRecent } = useEntityRecents()
  return useMutation({
    mutationFn: ({ entityId, name }: { entityId: string; name: string }) =>
      hierarchyApi.renameEntity(entityId, name),
    onSuccess: (entity) => {
      // The new name surfaces in the trigger, breadcrumb, and tree; drop cached
      // entity data so every view refetches it.
      void queryClient.invalidateQueries({ queryKey: entityKeys.root })
      // Convention: mutations reconcile recents with server truth (rename →
      // update the name; a future delete → drop the entry). *Adding* to recents
      // stays the selection flow's job (entity-selector handleSelect), which is
      // why create doesn't touch it here.
      renameRecent(entity.entityId, entity.name)
    },
  })
}

export function moveEntityErrorMessage(error: unknown, entityName: string) {
  return apiErrorMessage(error, 'Couldn’t move the entity. Try again.', {
    codes: {
      HIERARCHY_CYCLE_DETECTED: `Can't move ${entityName} under itself or one of its descendants.`,
      HIERARCHY_CHANGE_IN_PROGRESS:
        'This organization is being changed right now. Wait a moment and try again.',
      PENDING_INVITATIONS_CHANGED:
        'More pending invitations would now be cancelled. Review the updated list.',
    },
  })
}

export function useMoveImpact() {
  return useMutation({
    mutationFn: ({
      entityId,
      newParentEntityId,
    }: {
      entityId: string
      newParentEntityId: string
    }) => hierarchyApi.previewMove(entityId, newParentEntityId),
  })
}

export function useMoveEntity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      entityId,
      newParentEntityId,
      expectedCancelledInvitationIds,
    }: {
      entityId: string
      newParentEntityId: string
      expectedCancelledInvitationIds: string[]
    }) =>
      hierarchyApi.moveEntity(entityId, {
        newParentEntityId,
        expectedCancelledInvitationIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: entityKeys.root })
    },
  })
}

export function useCanEditEntity() {
  const { can } = usePermissions()
  return can(HIERARCHY_PERMISSIONS.edit)
}
